import type { OperationRequest } from '../api/types';
import { clientDuplicateGroups, isLookalike } from './duplicates';

/** One date's slice of the approval queue («Подтверждения» card). */
export interface ApprovalDateGroup {
  /** Business date, `YYYY-MM-DD` (already date-only on the wire). */
  date: string;
  /** Rows of that date: same store consecutive, then by submit time. */
  requests: OperationRequest[];
  /** Sum of the group's amounts, kopeks. */
  totalAmount: number;
  /** How many rows of this date look like a repeat of another entry. */
  duplicateCount: number;
}

/**
 * Group pending requests by their business date so same-day entries sit
 * together — oldest date first (queue order: clear the backlog top-down).
 * `date` is a `YYYY-MM-DD` string, so plain string compare sorts correctly.
 */
export function groupRequestsByDate(requests: OperationRequest[]): ApprovalDateGroup[] {
  // Look-alikes are detected across the WHOLE queue, not per date card: the
  // grouping is a display device, the duplicate question is not.
  const clientGroups = clientDuplicateGroups(requests);
  const byDate = new Map<string, OperationRequest[]>();
  for (const req of requests) {
    const list = byDate.get(req.date);
    if (list) list.push(req);
    else byDate.set(req.date, [req]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, list]) => ({
      date,
      requests: [...list].sort(
        (a, b) =>
          (a.storeName ?? '').localeCompare(b.storeName ?? '', 'ru') ||
          a.createdAt.localeCompare(b.createdAt),
      ),
      totalAmount: list.reduce((sum, req) => sum + req.amount, 0),
      duplicateCount: list.filter((req) => isLookalike(req, clientGroups)).length,
    }));
}
